import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Checkbox, FormControlLabel, Typography, Snackbar, Alert,
} from '@mui/material';
import DetalheCheque, { novoChequeDetalhe } from './DetalheCheque';
import {
  TIPO_FORMA_PAGTO_CARTAO, TIPO_FORMA_PAGTO_CHEQUE,
  calcularDataMinima, calcularDataMaxima, opcoesFormaPagamentoParaParcela,
  vencimentoParaIso, isoParaVencimento, redistribuirValores,
} from './passoTresController';

const MENSAGEM_CAMPO_OBRIGATORIO = 'Campo obrigatório';
const MENSAGEM_VALOR_INVALIDO = 'O valor deve ser maior que R$ 0,00';
const MENSAGEM_DATA_INVALIDA = 'Data fora do período permitido.';
const MENSAGEM_CHEQUE_OBRIGATORIO = 'Pelo menos um cheque deverá ser cadastrado';
const MENSAGEM_VALOR_CHEQUE_INVALIDO = 'O valor do cheque deve ser maior que R$ 0,00';

const CAMPOS_CHEQUE_OBRIGATORIOS = ['idBanco', 'numeroAgencia', 'numeroConta', 'numeroCheque'];

/**
 * Valida 1 cheque, marcando `error`/`errorMessage` em cada campo — mesmo
 * shape `{value, error, errorMessage}` já usado por `DetalheCheque.jsx`, para
 * que o próprio componente já exiba o erro sem lógica extra. Achado em code
 * review: a versão anterior só bloqueava `chequesDetalhes.length === 0`,
 * deixando um cheque com todos os campos vazios (valor incluído) passar
 * direto para `gerarAcordoPasso3` — a primeira chamada da feature que muta
 * dado real.
 */
function validarCheque(cheque) {
  const validado = CAMPOS_CHEQUE_OBRIGATORIOS.reduce((acc, campo) => ({
    ...acc,
    [campo]: cheque[campo].value
      ? { ...cheque[campo], error: false, errorMessage: '' }
      : { ...cheque[campo], error: true, errorMessage: MENSAGEM_CAMPO_OBRIGATORIO },
  }), { ...cheque });

  validado.valor = (!cheque.valor.value || Number(cheque.valor.value) <= 0)
    ? { ...cheque.valor, error: true, errorMessage: MENSAGEM_VALOR_CHEQUE_INVALIDO }
    : { ...cheque.valor, error: false, errorMessage: '' };

  const temErro = [...CAMPOS_CHEQUE_OBRIGATORIOS, 'valor'].some(campo => validado[campo].error);
  return { validado, temErro };
}

/**
 * Dialog de alterar 1 parcela — port de `DialogNovaAlterarParcela.jsx`/
 * `DetalheCheque.jsx`/`ChequeCampos.jsx` reais. Diferente da tela real, os 3
 * campos comuns (forma de pagamento/vencimento/valor) só aparecem uma vez —
 * a real duplica esse bloco entre o formulário normal e `DetalheCheque.jsx`;
 * aqui a seção de cheques é só a lista de cheques, anexada abaixo quando a
 * forma de pagamento é Cheque.
 */
export default function DialogAlterarParcela({
  open, parcela, parcelas, dadosPadraoForm, dadosResumo, dataFinalPasso3,
  prazoMaximoPrimeiroVencimento, formaPagamentoPasso2, formasPagamentoOptions, bancosOptions,
  onClose, onSalvar,
}) {
  const [formFields, setFormFields] = useState({
    formaPagamento: '', dataVencimento: '', valor: '', redistribuirValores: true,
  });
  const [chequesDetalhes, setChequesDetalhes] = useState([novoChequeDetalhe()]);
  const [erros, setErros] = useState({});
  const [erroGeral, setErroGeral] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormFields({
      formaPagamento: parcela.formaPagamento,
      dataVencimento: vencimentoParaIso(parcela.vencimento),
      valor: parcela.valor,
      redistribuirValores: true,
    });
    setChequesDetalhes(parcela.chequeDetalhe?.length > 0 ? parcela.chequeDetalhe : [novoChequeDetalhe()]);
    setErros({});
    setErroGeral('');
  }, [open, parcela]);

  const ehUltimaParcela = parcela.nParcela === parcelas.length;
  const ehPrimeiraParcela = parcela.nParcela === 1;
  const ehCheque = formFields.formaPagamento === TIPO_FORMA_PAGTO_CHEQUE;

  const dataMinima = calcularDataMinima({
    nParcela: parcela.nParcela,
    dataPrimeiroVencimentoISO: dadosPadraoForm.dataPrimeiroVencimento,
  });
  const dataMaxima = calcularDataMaxima({
    nParcela: parcela.nParcela,
    dataPrimeiroVencimentoISO: dadosPadraoForm.dataPrimeiroVencimento,
    dataFinalISO: vencimentoParaIso(dataFinalPasso3),
    prazoMaximoPrimeiroVencimento,
  });

  const opcoesFormaPagamento = opcoesFormaPagamentoParaParcela(
    parcela.formaPagamento,
    formaPagamentoPasso2,
    formasPagamentoOptions,
  );

  const onChangeCampo = (event) => {
    const { name, value } = event.target;
    setFormFields(atual => ({ ...atual, [name]: value }));
    setErros(atual => ({ ...atual, [name]: '' }));
  };

  const onChangeRedistribuir = (event) => {
    setFormFields(atual => ({ ...atual, redistribuirValores: event.target.checked }));
  };

  const validar = () => {
    const proximosErros = {};
    if (!formFields.formaPagamento && formFields.formaPagamento !== 0) {
      proximosErros.formaPagamento = MENSAGEM_CAMPO_OBRIGATORIO;
    }
    if (!formFields.dataVencimento) {
      proximosErros.dataVencimento = MENSAGEM_CAMPO_OBRIGATORIO;
    } else {
      const dataEscolhida = new Date(formFields.dataVencimento);
      if (dataEscolhida < dataMinima || dataEscolhida > dataMaxima) {
        proximosErros.dataVencimento = MENSAGEM_DATA_INVALIDA;
      }
    }
    if (!ehCheque) {
      if (!formFields.valor || Number(formFields.valor) <= 0) {
        proximosErros.valor = MENSAGEM_VALOR_INVALIDO;
      }
    }
    return proximosErros;
  };

  const onSubmit = (event) => {
    event.preventDefault();

    const proximosErros = validar();
    if (Object.keys(proximosErros).length > 0) {
      setErros(proximosErros);
      return;
    }
    if (ehCheque) {
      if (chequesDetalhes.length === 0) {
        setErroGeral(MENSAGEM_CHEQUE_OBRIGATORIO);
        return;
      }
      const resultadosValidacao = chequesDetalhes.map(validarCheque);
      if (resultadosValidacao.some(resultado => resultado.temErro)) {
        setChequesDetalhes(resultadosValidacao.map(resultado => resultado.validado));
        return;
      }
    }

    const vencimentoBr = isoParaVencimento(formFields.dataVencimento);
    const parcelaEditada = {
      ...parcela,
      formaPagamento: formFields.formaPagamento,
      vencimento: vencimentoBr,
      valor: ehCheque
        ? chequesDetalhes.reduce((total, cheque) => total + (Number(cheque.valor.value) || 0), 0)
        : Number(formFields.valor),
      redistribuirValores: formFields.redistribuirValores,
      chequeDetalhe: ehCheque ? chequesDetalhes : [],
    };

    const deveRedistribuir = formFields.redistribuirValores && !ehUltimaParcela;
    const novasParcelas = deveRedistribuir
      ? redistribuirValores({
        parcelas,
        parcelaEditada,
        valorTotalAcordo: dadosResumo.valorTotal,
        quantidadeParcelasFormulario: dadosPadraoForm.quantidadeParcelas,
      })
      : parcelas.map(item => (item.nParcela === parcela.nParcela ? parcelaEditada : item));

    onSalvar(novasParcelas);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth hideBackdrop>
        <DialogTitle>Alterar Parcela</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Forma de Pagamento"
            name="formaPagamento"
            value={formFields.formaPagamento}
            onChange={onChangeCampo}
            disabled={parcelas.length > 1 && !ehPrimeiraParcela}
            error={Boolean(erros.formaPagamento)}
            helperText={erros.formaPagamento}
            size="small"
            sx={{ minWidth: 220, mr: 2, mt: 1 }}
          >
            {opcoesFormaPagamento.map(opcao => (
              <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Vencimento"
            name="dataVencimento"
            value={formFields.dataVencimento}
            onChange={onChangeCampo}
            disabled={formFields.formaPagamento === TIPO_FORMA_PAGTO_CARTAO || ehPrimeiraParcela}
            error={Boolean(erros.dataVencimento)}
            helperText={erros.dataVencimento}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ mr: 2, mt: 1 }}
          />
          <TextField
            type="number"
            label="Valor"
            name="valor"
            value={ehCheque
              ? chequesDetalhes.reduce((total, cheque) => total + (Number(cheque.valor.value) || 0), 0).toFixed(2)
              : formFields.valor}
            onChange={onChangeCampo}
            disabled={ehCheque}
            error={Boolean(erros.valor)}
            helperText={erros.valor}
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mt: 1 }}
          />

          <FormControlLabel
            sx={{ display: 'block', mt: 2 }}
            control={(
              <Checkbox
                checked={!ehUltimaParcela && formFields.redistribuirValores}
                disabled={ehUltimaParcela}
                onChange={onChangeRedistribuir}
              />
            )}
            label="Redistribuir os valores para as parcelas seguintes"
          />

          {ehCheque && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Cheques</Typography>
              <DetalheCheque
                chequesDetalhes={chequesDetalhes}
                onChange={setChequesDetalhes}
                bancosOptions={bancosOptions}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erroGeral)}
        autoHideDuration={4000}
        onClose={() => setErroGeral('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setErroGeral('')}>
          {erroGeral}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogAlterarParcela.propTypes = {
  open: PropTypes.bool,
  parcela: PropTypes.object,
  parcelas: PropTypes.array,
  dadosPadraoForm: PropTypes.object,
  dadosResumo: PropTypes.object,
  dataFinalPasso3: PropTypes.string,
  prazoMaximoPrimeiroVencimento: PropTypes.number,
  formaPagamentoPasso2: PropTypes.number,
  formasPagamentoOptions: PropTypes.array,
  bancosOptions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSalvar: PropTypes.func.isRequired,
};

DialogAlterarParcela.defaultProps = {
  open: false,
  parcela: {},
  parcelas: [],
  dadosPadraoForm: {},
  dadosResumo: {},
  dataFinalPasso3: '',
  prazoMaximoPrimeiroVencimento: 0,
  formaPagamentoPasso2: null,
  formasPagamentoOptions: [],
  bancosOptions: [],
};
